import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchAdminRisks } from '@/api/(admin)/risk-management';
import { useTable } from '@/hooks/useTable';
import {
  buildListQs,
  type ListParams,
  normalizeListParams,
} from '../-utils/listParams';

/** Everything under this prefix — what the write paths invalidate. */
export const adminRisksQueryKey = ['management', 'risks'] as const;

export function adminRisksQueryOptions(params: ListParams) {
  return queryOptions({
    queryKey: [
      ...adminRisksQueryKey,
      'list',
      normalizeListParams(params),
    ] as const,
    queryFn: () => fetchAdminRisks(buildListQs(params)),
  });
}

/**
 * The risk catalogue, paged and filtered through the URL. Never sorted: the
 * endpoint always orders by creation and rejects a `sort` param.
 */
export function useAdminRisks() {
  // No `tab`: this list has its own route, so its search params are its own.
  const table = useTable('management-risks', { mode: 'url' });

  const params: ListParams = {
    page: table.page,
    limit: table.limit,
    filters: table.filters,
  };

  const query = useQuery({
    ...adminRisksQueryOptions(params),
    placeholderData: prev => prev,
  });

  return {
    rows: query.data?.data ?? [],
    total: query.data?.meta?.totalCount ?? 0,
    isLoading: query.isLoading,
    filters: table.filters,
    pagination: { pageIndex: table.page - 1, pageSize: table.limit },
    onFiltersChange: table.onFiltersChange,
    onPaginationChange: table.onPaginationChange,
  };
}
